import React, { useState, useEffect } from 'react';
import { FaStar, FaMapMarkerAlt, FaCar, FaGlobe, FaSearch, FaFilter, FaMoon, FaCompass, FaMedal, FaInfoCircle, FaExternalLinkAlt } from 'react-icons/fa';
import { getDarkSkyLocations, getNearestDarkSky, geocodeLocation } from '../../api/client';

const DarkSkyFinder = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filters, setFilters] = useState({
    country: '',
    maxBortle: 9,  // Default to "Any" to show all locations
    maxDistance: 500
  });
  const [viewMode, setViewMode] = useState('all'); // 'all', 'nearby', or 'search'

  // City/State search
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searchLocationName, setSearchLocationName] = useState('');

  // Country options
  const countries = ['USA', 'Canada', 'UK', 'Australia', 'New Zealand', 'Germany', 'France', 'Spain', 'Ireland', 'Namibia', 'Chile'];

  // Bortle scale descriptions
  const bortleDescriptions = {
    1: { label: 'Excellent', color: 'bg-emerald-500', desc: 'Zodiacal light visible, Milky Way casts shadows' },
    2: { label: 'Truly Dark', color: 'bg-green-500', desc: 'Milky Way highly detailed, airglow visible' },
    3: { label: 'Rural', color: 'bg-lime-500', desc: 'Milky Way appears complex with detail' },
    4: { label: 'Transition', color: 'bg-yellow-500', desc: 'Milky Way visible but washed out near horizon' },
    5: { label: 'Suburban', color: 'bg-orange-500', desc: 'Milky Way faint, only visible overhead' },
    6: { label: 'Bright Suburban', color: 'bg-orange-600', desc: 'Milky Way invisible, sky glow obvious' },
    7: { label: 'Suburban/Urban', color: 'bg-red-400', desc: 'Sky has overall grayish-white hue' },
    8: { label: 'City Sky', color: 'bg-red-500', desc: 'Only brightest stars visible' },
    9: { label: 'Inner City', color: 'bg-red-600', desc: 'Only Moon, planets, few stars visible' }
  };

  // Get user's location
  const getUserLocation = () => {
    setGettingLocation(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setSearchLocationName('');
          setGettingLocation(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setGettingLocation(false);
          alert('Could not get your location. Please enable location services.');
        },
        { timeout: 10000 }
      );
    } else {
      setGettingLocation(false);
      alert('Geolocation is not supported by your browser.');
    }
  };

  // Search by city/state
  const handleCitySearch = async () => {
    if (!searchCity.trim()) {
      setSearchError('Please enter a city name');
      return;
    }

    setSearchError('');
    setGettingLocation(true);

    try {
      const data = await geocodeLocation(searchCity.trim(), searchState.trim() || null, searchCountry.trim() || null);

      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        setUserLocation({
          lat: result.lat,
          lng: result.lng
        });
        setSearchLocationName(result.name);
        setViewMode('nearby');
      } else {
        setSearchError('Location not found. Try a different city name.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setSearchError('Failed to search location. Please try again.');
    } finally {
      setGettingLocation(false);
    }
  };

  // Fetch locations
  const fetchLocations = async () => {
    setLoading(true);
    try {
      const params = {
        max_bortle: filters.maxBortle,
        limit: 50
      };

      if (filters.country) {
        params.country = filters.country;
      }

      if (userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
      }

      const data = await getDarkSkyLocations(params);
      setLocations(data.locations || []);
    } catch (error) {
      console.error('Error fetching dark sky locations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch nearby locations
  const fetchNearby = async () => {
    if (!userLocation) {
      getUserLocation();
      return;
    }

    setLoading(true);
    try {
      const data = await getNearestDarkSky(userLocation.lat, userLocation.lng, filters.maxDistance);
      setLocations(data.nearest_locations || []);
    } catch (error) {
      console.error('Error fetching nearby locations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchLocations();
  }, []);

  // Refetch when filters change
  useEffect(() => {
    if (viewMode === 'all') {
      fetchLocations();
    } else if ((viewMode === 'nearby' || viewMode === 'search') && userLocation) {
      fetchNearby();
    }
  }, [filters, userLocation, viewMode]);

  // Render certification badge
  const renderCertification = (cert) => {
    const colors = {
      'Gold Tier': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Silver Tier': 'bg-gray-100 text-gray-700 border-gray-300',
      'Starlight': 'bg-blue-100 text-blue-800 border-blue-300',
      'Uncertified': 'bg-neutral-100 text-neutral-600 border-neutral-300'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${colors[cert] || colors['Uncertified']}`}>
        {cert}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Stars animation */}
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                opacity: Math.random() * 0.8 + 0.2
              }}
            />
          ))}
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaMoon className="text-2xl text-yellow-300" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Dark Sky Finder</h2>
              <p className="text-white/70">Find the best stargazing locations</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl font-bold">{locations.length}</div>
              <div className="text-white/70 text-sm">Locations Found</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl font-bold">
                {locations.filter(l => l.bortle <= 2).length}
              </div>
              <div className="text-white/70 text-sm">Truly Dark Sites</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl font-bold">
                {userLocation ? `${userLocation.lat.toFixed(1)}°` : '--'}
              </div>
              <div className="text-white/70 text-sm">Your Latitude</div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* View Mode Toggle */}
          <div className="flex bg-neutral-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'all'
                  ? 'bg-white shadow-sm text-primary-600'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaGlobe className="inline mr-2" />
              All Locations
            </button>
            <button
              onClick={() => {
                setViewMode('nearby');
                if (!userLocation) getUserLocation();
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'nearby'
                  ? 'bg-white shadow-sm text-primary-600'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaCompass className="inline mr-2" />
              Near Me
            </button>
            <button
              onClick={() => setViewMode('search')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'search'
                  ? 'bg-white shadow-sm text-primary-600'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaSearch className="inline mr-2" />
              By City
            </button>
          </div>

          {/* Get Location Button (only show when not in search mode) */}
          {viewMode !== 'search' && (
            <button
              onClick={getUserLocation}
              disabled={gettingLocation}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              {gettingLocation ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <FaMapMarkerAlt />
              )}
              {userLocation ? 'Update Location' : 'Get My Location'}
            </button>
          )}
        </div>

        {/* City/State Search (only show in search mode) */}
        {viewMode === 'search' && (
          <div className="mb-6 p-4 bg-indigo-50 rounded-xl">
            <h4 className="text-sm font-medium text-indigo-900 mb-3">Search by Location</h4>
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[120px]">
                <input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  placeholder="City (e.g., Atacama)"
                  className="w-full px-4 py-2.5 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  onKeyPress={(e) => e.key === 'Enter' && handleCitySearch()}
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <input
                  type="text"
                  value={searchState}
                  onChange={(e) => setSearchState(e.target.value)}
                  placeholder="Region (e.g., Antofagasta)"
                  className="w-full px-4 py-2.5 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  onKeyPress={(e) => e.key === 'Enter' && handleCitySearch()}
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <input
                  type="text"
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                  placeholder="Country (e.g., Chile)"
                  className="w-full px-4 py-2.5 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  onKeyPress={(e) => e.key === 'Enter' && handleCitySearch()}
                />
              </div>
              <button
                onClick={handleCitySearch}
                disabled={gettingLocation}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {gettingLocation ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <FaSearch />
                )}
                Search
              </button>
            </div>
            {searchError && (
              <p className="text-red-600 text-sm mt-2">{searchError}</p>
            )}
            {searchLocationName && (
              <p className="text-indigo-700 text-sm mt-2">
                <FaMapMarkerAlt className="inline mr-1" />
                Showing results near {searchLocationName}
              </p>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Country Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              <FaFilter className="inline mr-2" />
              Country
            </label>
            <select
              value={filters.country}
              onChange={(e) => setFilters({ ...filters, country: e.target.value })}
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
            >
              <option value="">All Countries</option>
              {countries.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Bortle Scale Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              <FaStar className="inline mr-2" />
              Max Bortle Scale
            </label>
            <select
              value={filters.maxBortle}
              onChange={(e) => setFilters({ ...filters, maxBortle: parseInt(e.target.value) })}
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
            >
              <option value={9}>Any (Show All)</option>
              <option value={1}>1 - Excellent (Darkest)</option>
              <option value={2}>2 - Truly Dark</option>
              <option value={3}>3 - Rural Sky</option>
              <option value={4}>4 - Rural/Suburban Transition</option>
              <option value={5}>5 - Suburban Sky</option>
              <option value={6}>6 - Bright Suburban</option>
              <option value={7}>7 - Suburban/Urban Transition</option>
            </select>
          </div>

          {/* Distance Filter (for nearby mode) */}
          {viewMode === 'nearby' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                <FaCar className="inline mr-2" />
                Max Distance
              </label>
              <select
                value={filters.maxDistance}
                onChange={(e) => setFilters({ ...filters, maxDistance: parseInt(e.target.value) })}
                className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              >
                <option value={100}>100 km (62 mi)</option>
                <option value={250}>250 km (155 mi)</option>
                <option value={500}>500 km (310 mi)</option>
                <option value={1000}>1000 km (620 mi)</option>
              </select>
            </div>
          )}
        </div>

        {/* Bortle Scale Color Legend */}
        <div className="mt-6 pt-4 border-t border-neutral-100">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-neutral-700 flex items-center gap-2">
              <FaStar className="text-indigo-500" />
              Bortle Scale Legend
            </h4>
            <span className="text-xs text-neutral-500">Lower = Darker Sky</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(bortleDescriptions).map(([scale, info]) => (
              <div
                key={scale}
                className="flex items-center gap-1.5 px-2 py-1 bg-neutral-50 rounded-lg"
                title={info.desc}
              >
                <div className={`w-3 h-3 rounded-full ${info.color}`}></div>
                <span className="text-xs font-medium text-neutral-700">{scale}</span>
                <span className="text-xs text-neutral-500 hidden sm:inline">- {info.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-neutral-600">Searching the night sky...</p>
          </div>
        ) : locations.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <FaStar className="text-5xl text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-700 mb-2">No Locations Found</h3>
            <p className="text-neutral-500">Try adjusting your filters or expanding the search area.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {locations.map((location, idx) => (
              <div
                key={idx}
                className={`bg-white rounded-2xl shadow-sm hover:shadow-lg border transition-all cursor-pointer ${
                  selectedLocation === idx ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-neutral-100'
                }`}
                onClick={() => setSelectedLocation(selectedLocation === idx ? null : idx)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-neutral-800">{location.name}</h3>
                        {location.bortle === 1 && (
                          <FaMedal className="text-yellow-500" title="Darkest Site" />
                        )}
                      </div>
                      <p className="text-sm text-neutral-500">
                        {location.state}, {location.country}
                      </p>
                    </div>
                    {renderCertification(location.certification)}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Bortle Rating */}
                    <div className="bg-neutral-50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-3 h-3 rounded-full ${bortleDescriptions[location.bortle]?.color || 'bg-gray-400'}`} />
                        <span className="text-xs font-medium text-neutral-500">Bortle {location.bortle}</span>
                      </div>
                      <p className="text-sm font-semibold text-neutral-700">
                        {bortleDescriptions[location.bortle]?.label || 'Unknown'}
                      </p>
                    </div>

                    {/* Distance */}
                    {location.distance_km && (
                      <div className="bg-neutral-50 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <FaCar className="text-neutral-400 text-xs" />
                          <span className="text-xs font-medium text-neutral-500">Distance</span>
                        </div>
                        <p className="text-sm font-semibold text-neutral-700">
                          {location.distance_km < 100
                            ? `${location.distance_km} km`
                            : `${Math.round(location.distance_km)} km`}
                          <span className="text-neutral-400 ml-1">
                            ({location.distance_miles} mi)
                          </span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Type Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-medium">
                      {location.type}
                    </span>

                    {/* Quick Links */}
                    <div className="flex items-center gap-2">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaMapMarkerAlt />
                        Map
                      </a>
                      <a
                        href={`https://www.google.com/search?q=${encodeURIComponent(location.name + ' stargazing')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaExternalLinkAlt />
                        Info
                      </a>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedLocation === idx && (
                    <div className="mt-4 pt-4 border-t border-neutral-100 animate-fade-in">
                      <p className="text-sm text-neutral-600 mb-3">
                        {bortleDescriptions[location.bortle]?.desc || 'Great stargazing location'}
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs text-neutral-500">
                        <span>Lat: {location.lat.toFixed(4)}</span>
                        <span>|</span>
                        <span>Lng: {location.lng.toFixed(4)}</span>
                        {location.drive_time_estimate && (
                          <>
                            <span>|</span>
                            <span>Est. drive: {location.drive_time_estimate}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="bg-indigo-50 rounded-2xl p-6 flex items-start gap-4">
        <FaInfoCircle className="text-indigo-500 text-xl flex-shrink-0 mt-1" />
        <div>
          <h4 className="font-semibold text-indigo-900 mb-1">About the Bortle Scale</h4>
          <p className="text-sm text-indigo-700">
            The Bortle Dark-Sky Scale measures light pollution on a scale of 1 to 9.
            Class 1 represents the darkest skies on Earth where the zodiacal light is visible,
            while Class 9 is an inner-city sky where only the Moon, planets, and a few bright stars are visible.
            For best astrophotography, look for sites rated Bortle 1-3.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DarkSkyFinder;
